// Mantenimiento M1 (M50): tipos, catálogo de categorías con sus campos técnicos y etiquetas del inventario de activos.
// Registro único de activos (K10) sobre el que se construirán la planificación (M2) y las órdenes de trabajo (M3).
// Los equipos de emergencia (extintor, lámpara de emergencia, detector, pulsador, botiquín y gabinete) son categorías
// del inventario. Los textos de este archivo los ve el cliente: sin nombres internos ni códigos de decisión.

export type Categoria =
  | 'maquina' | 'ventilacion' | 'electrico' | 'presion' | 'izaje' | 'vehiculo' | 'herramienta' | 'luminaria' | 'infraestructura'
  | 'extintor' | 'lampara_emergencia' | 'detector' | 'pulsador' | 'botiquin' | 'gabinete' | 'otro';
export type Criticidad = 'baja' | 'media' | 'alta' | 'critica';
export type EstadoOperativo = 'operativo' | 'con_observaciones' | 'fuera_servicio' | 'en_mantenimiento';
export type Aptitud = 'apto' | 'no_apto';
export type TipoPermiso = 'altura' | 'caliente' | 'confinado' | 'electrico' | 'quimico' | 'loto' | 'excavacion' | 'maquinaria' | 'vias';
export type TipoDocumento = 'manual' | 'ficha_tecnica' | 'certificado' | 'garantia' | 'informe' | 'foto' | 'otro';
export type EventoHistorial = 'creado' | 'editado' | 'codigo' | 'estado' | 'aptitud' | 'archivado' | 'restaurado' | 'documento'
  | 'documento_eliminado' | 'inspeccion';

export interface PuntoAislamiento { tipo: string; ubicacion?: string }

export interface FilaActivo {
  id: string;
  company_id: string;
  code: string;
  name: string;
  category: Categoria;
  manufacturer: string | null;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  manufacture_year: number | null;
  description: string | null;
  photo_path: string | null;
  site_id: string | null;
  area: string | null;
  location_detail: string | null;
  tech: Record<string, string | number | null>;
  acquired_on: string | null;
  installed_on: string | null;
  useful_life_years: number | null;
  manual_ref: string | null;
  responsible_worker_id: string | null;
  responsible_user_id: string | null;
  external_provider: string | null;
  hazards: string[];
  required_epp_ids: string[];
  isolation_points: PuntoAislamiento[];
  permit_type: TipoPermiso | null;
  procedure_ref: string | null;
  operating_restrictions: string | null;
  criticality: Criticidad;
  operational_status: EstadoOperativo;
  safety_status: Aptitud;
  emergency_sector_id: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export const CAMPOS_ACTIVO = 'id, company_id, code, name, category, manufacturer, brand, model, serial_number, manufacture_year, description, '
  + 'photo_path, site_id, area, location_detail, tech, acquired_on, installed_on, useful_life_years, manual_ref, responsible_worker_id, '
  + 'responsible_user_id, external_provider, hazards, required_epp_ids, isolation_points, permit_type, procedure_ref, operating_restrictions, '
  + 'criticality, operational_status, safety_status, emergency_sector_id, archived_at, created_at, updated_at';

export interface FilaDocumento {
  id: string;
  asset_id: string;
  kind: TipoDocumento;
  title: string | null;
  file_path: string;
  file_name: string;
  file_mime: string;
  file_size: number;
  valid_until: string | null;
  created_at: string;
}

export interface FilaHistorial {
  id: string;
  asset_id: string;
  event: EventoHistorial;
  from_value: string | null;
  to_value: string | null;
  reason: string | null;
  detail: Record<string, unknown>;
  actor_name: string | null;
  created_at: string;
}

// ── Catálogo de categorías ──
export type TipoCampo = 'numero' | 'texto' | 'fecha' | 'opcion';
export interface CampoTecnico {
  clave: string;
  etiqueta: string;
  tipo: TipoCampo;
  unidad?: string;
  opciones?: { valor: string; etiqueta: string }[];
  ayuda?: string;
}

const ENERGIAS = [
  { valor: 'electrica', etiqueta: 'Eléctrica' }, { valor: 'neumatica', etiqueta: 'Neumática' }, { valor: 'hidraulica', etiqueta: 'Hidráulica' },
  { valor: 'mecanica', etiqueta: 'Mecánica' }, { valor: 'termica', etiqueta: 'Térmica' }, { valor: 'quimica', etiqueta: 'Química' },
  { valor: 'combustible', etiqueta: 'Combustible' }, { valor: 'gravitacional', etiqueta: 'Gravitacional' }, { valor: 'otra', etiqueta: 'Otra' },
];
export const TIPOS_ENERGIA = ENERGIAS;
export const etiquetaEnergia = (v: string) => ENERGIAS.find(e => e.valor === v)?.etiqueta ?? v;

const potenciaKw: CampoTecnico = { clave: 'potencia_kw', etiqueta: 'Potencia', tipo: 'numero', unidad: 'kW' };
const voltaje: CampoTecnico = { clave: 'voltaje_v', etiqueta: 'Voltaje', tipo: 'numero', unidad: 'V' };
const energia: CampoTecnico = { clave: 'tipo_energia', etiqueta: 'Tipo de energía', tipo: 'opcion', opciones: ENERGIAS };
const capacidadKg: CampoTecnico = { clave: 'capacidad_kg', etiqueta: 'Capacidad', tipo: 'numero', unidad: 'kg' };
const ultimaPrueba = (ayuda: string): CampoTecnico => ({ clave: 'ultima_prueba', etiqueta: 'Última prueba de funcionamiento', tipo: 'fecha', ayuda });

export const AGENTES_EXTINTOR = [
  { valor: 'pqs', etiqueta: 'Polvo químico seco (PQS)' }, { valor: 'co2', etiqueta: 'Dióxido de carbono (CO₂)' },
  { valor: 'agua', etiqueta: 'Agua presurizada' }, { valor: 'espuma', etiqueta: 'Espuma (AFFF)' },
  { valor: 'acetato_potasio', etiqueta: 'Agente húmedo: acetato de potasio (clase K)' }, { valor: 'halogenado', etiqueta: 'Halogenado' },
  { valor: 'agente_limpio', etiqueta: 'Agente limpio no halogenado' }, { valor: 'otro', etiqueta: 'Otro' },
];

// ── Plazos de los extintores (NTE INEN 739:2016 y AM 1257) ──
// Columnas de asset_extinguisher_intervals (M50). El valor de la norma es el máximo: la empresa puede acortarlo, no
// alargarlo (la base lo controla con un CHECK). Sin fila de la empresa valen los de la norma.
export type ClavePlazoExtintor =
  | 'visual_inspection_days' | 'maintenance_months' | 'internal_co2_months' | 'internal_water_months' | 'internal_dry_chemical_months'
  | 'internal_halogenated_months' | 'internal_cartridge_months' | 'internal_foam_months' | 'internal_wet_agent_months'
  | 'hydrostatic_months' | 'non_rechargeable_life_months' | 'co2_hose_months';
export interface DefPlazoExtintor { clave: ClavePlazoExtintor; etiqueta: string; unidad: 'dias' | 'meses'; norma: number; fuente: string }
export const PLAZOS_EXTINTOR: DefPlazoExtintor[] = [
  { clave: 'visual_inspection_days', etiqueta: 'Inspección visual', unidad: 'dias', norma: 31, fuente: 'NTE INEN 739:2016: cada 31 días como máximo' },
  { clave: 'maintenance_months', etiqueta: 'Mantenimiento', unidad: 'meses', norma: 12, fuente: 'NTE INEN 739:2016: anual' },
  { clave: 'internal_co2_months', etiqueta: 'Mantenimiento interno: CO₂', unidad: 'meses', norma: 60, fuente: 'NTE INEN 739:2016, Tabla 1: cada 5 años' },
  { clave: 'internal_water_months', etiqueta: 'Mantenimiento interno: agua presurizada', unidad: 'meses', norma: 60, fuente: 'NTE INEN 739:2016, Tabla 1: cada 5 años' },
  { clave: 'internal_dry_chemical_months', etiqueta: 'Mantenimiento interno: químico seco presurizado de acero dulce', unidad: 'meses', norma: 72,
    fuente: 'NTE INEN 739:2016, Tabla 1: cada 6 años' },
  { clave: 'internal_halogenated_months', etiqueta: 'Mantenimiento interno: halogenados', unidad: 'meses', norma: 72, fuente: 'NTE INEN 739:2016, Tabla 1: cada 6 años' },
  { clave: 'internal_cartridge_months', etiqueta: 'Mantenimiento interno: de cartucho', unidad: 'meses', norma: 12, fuente: 'NTE INEN 739:2016, Tabla 1: cada año' },
  { clave: 'internal_foam_months', etiqueta: 'Mantenimiento interno: espuma AFFF', unidad: 'meses', norma: 36, fuente: 'NTE INEN 739:2016, Tabla 1: cada 3 años' },
  { clave: 'internal_wet_agent_months', etiqueta: 'Mantenimiento interno: agente húmedo', unidad: 'meses', norma: 12, fuente: 'NTE INEN 739:2016, Tabla 1: anual' },
  { clave: 'hydrostatic_months', etiqueta: 'Prueba hidrostática', unidad: 'meses', norma: 72,
    fuente: 'AM 1257: cada 6 años; vencida, no se puede registrar una recarga' },
  { clave: 'non_rechargeable_life_months', etiqueta: 'Extintor no recargable: fuera de servicio', unidad: 'meses', norma: 144,
    fuente: 'NTE INEN 739:2016: a los 12 años de su fabricación' },
  { clave: 'co2_hose_months', etiqueta: 'Prueba de conductividad de la manguera (CO₂)', unidad: 'meses', norma: 12, fuente: 'NTE INEN 739:2016: anual' },
];
export type PlazosExtintor = Record<ClavePlazoExtintor, number>;
export const PLAZOS_NORMA = Object.fromEntries(PLAZOS_EXTINTOR.map(p => [p.clave, p.norma])) as PlazosExtintor;
/** Texto de un plazo: "31 días", "12 meses", "5 años". */
export function textoPlazo(n: number, unidad: 'dias' | 'meses'): string {
  if (unidad === 'dias') return `${n} ${n === 1 ? 'día' : 'días'}`;
  if (n % 12 === 0) return `${n / 12} ${n === 12 ? 'año' : 'años'}`;
  return `${n} ${n === 1 ? 'mes' : 'meses'}`;
}

export interface DefCategoria {
  etiqueta: string;
  prefijo: string;
  emergencia: boolean;
  campos: CampoTecnico[];
}

export const CATEGORIAS: Record<Categoria, DefCategoria> = {
  maquina: { etiqueta: 'Máquina o equipo de producción', prefijo: 'MAQ', emergencia: false, campos: [potenciaKw, voltaje, energia,
    { clave: 'capacidad', etiqueta: 'Capacidad o producción', tipo: 'texto' }] },
  ventilacion: { etiqueta: 'Ventilación, extracción o climatización', prefijo: 'VEN', emergencia: false, campos: [
    { clave: 'caudal_m3h', etiqueta: 'Caudal', tipo: 'numero', unidad: 'm³/h' }, potenciaKw, voltaje] },
  electrico: { etiqueta: 'Tablero o instalación eléctrica', prefijo: 'ELE', emergencia: false, campos: [voltaje,
    { clave: 'corriente_a', etiqueta: 'Corriente', tipo: 'numero', unidad: 'A' }, potenciaKw] },
  presion: { etiqueta: 'Equipo a presión (compresor, caldero, recipiente)', prefijo: 'PRE', emergencia: false, campos: [
    { clave: 'presion_bar', etiqueta: 'Presión máxima de trabajo', tipo: 'numero', unidad: 'bar' },
    { clave: 'capacidad_l', etiqueta: 'Capacidad', tipo: 'numero', unidad: 'L' }, energia] },
  izaje: { etiqueta: 'Equipo de izaje', prefijo: 'IZA', emergencia: false, campos: [capacidadKg, energia] },
  vehiculo: { etiqueta: 'Vehículo o montacargas', prefijo: 'VEH', emergencia: false, campos: [
    { clave: 'placa', etiqueta: 'Placa', tipo: 'texto' }, capacidadKg, energia] },
  herramienta: { etiqueta: 'Herramienta eléctrica o manual', prefijo: 'HER', emergencia: false, campos: [
    { clave: 'potencia_w', etiqueta: 'Potencia', tipo: 'numero', unidad: 'W' }, voltaje, energia] },
  luminaria: { etiqueta: 'Luminaria', prefijo: 'LUM', emergencia: false, campos: [
    { clave: 'potencia_w', etiqueta: 'Potencia', tipo: 'numero', unidad: 'W' },
    { clave: 'tecnologia', etiqueta: 'Tecnología', tipo: 'opcion', opciones: [{ valor: 'led', etiqueta: 'LED' },
      { valor: 'fluorescente', etiqueta: 'Fluorescente' }, { valor: 'halogeno', etiqueta: 'Halógeno o descarga' }, { valor: 'otra', etiqueta: 'Otra' }] },
    { clave: 'ultimo_mantenimiento', etiqueta: 'Última limpieza o mantenimiento', tipo: 'fecha' }] },
  infraestructura: { etiqueta: 'Instalación o infraestructura', prefijo: 'INF', emergencia: false, campos: [] },
  extintor: { etiqueta: 'Extintor', prefijo: 'EXT', emergencia: true, campos: [
    { clave: 'agente', etiqueta: 'Agente extintor', tipo: 'opcion', opciones: AGENTES_EXTINTOR },
    { clave: 'capacidad', etiqueta: 'Capacidad', tipo: 'numero', unidad: 'lb' },
    { clave: 'presurizacion', etiqueta: 'Presurización', tipo: 'opcion',
      opciones: [{ valor: 'almacenada', etiqueta: 'Presión almacenada' }, { valor: 'cartucho', etiqueta: 'De cartucho' }] },
    { clave: 'recargable', etiqueta: 'Recargable', tipo: 'opcion', opciones: [{ valor: 'si', etiqueta: 'Sí' }, { valor: 'no', etiqueta: 'No (descartable)' }] },
    { clave: 'fecha_fabricacion', etiqueta: 'Fecha de fabricación', tipo: 'fecha', ayuda: 'Sin ella se usa el año de fabricación (1 de enero).' },
    { clave: 'ultima_inspeccion_visual', etiqueta: 'Última inspección visual', tipo: 'fecha' },
    { clave: 'ultimo_mantenimiento', etiqueta: 'Último mantenimiento', tipo: 'fecha' },
    { clave: 'ultimo_mantenimiento_interno', etiqueta: 'Último mantenimiento interno', tipo: 'fecha',
      ayuda: 'Sin él, el primero se cuenta desde la fabricación.' },
    { clave: 'ultima_recarga', etiqueta: 'Última recarga', tipo: 'fecha',
      ayuda: 'No se puede registrar con la prueba hidrostática vencida a esa fecha, ni en un extintor no recargable.' },
    { clave: 'prueba_hidrostatica', etiqueta: 'Última prueba hidrostática', tipo: 'fecha', ayuda: 'Sin ella, la primera se cuenta desde la fabricación.' },
    { clave: 'prueba_manguera_co2', etiqueta: 'Última prueba de conductividad de la manguera (CO₂)', tipo: 'fecha' }] },
  lampara_emergencia: { etiqueta: 'Lámpara de emergencia', prefijo: 'LEM', emergencia: true, campos: [
    { clave: 'autonomia_min', etiqueta: 'Autonomía', tipo: 'numero', unidad: 'min' }, ultimaPrueba('Prueba mensual sugerida.')] },
  detector: { etiqueta: 'Detector (humo, calor o gas)', prefijo: 'DET', emergencia: true, campos: [
    { clave: 'tipo_detector', etiqueta: 'Tipo', tipo: 'opcion', opciones: [{ valor: 'humo', etiqueta: 'Humo' }, { valor: 'calor', etiqueta: 'Calor' },
      { valor: 'gas', etiqueta: 'Gas' }, { valor: 'otro', etiqueta: 'Otro' }] }, ultimaPrueba('Prueba anual sugerida.')] },
  pulsador: { etiqueta: 'Pulsador de alarma', prefijo: 'PUL', emergencia: true, campos: [ultimaPrueba('Prueba anual sugerida.')] },
  botiquin: { etiqueta: 'Botiquín', prefijo: 'BOT', emergencia: true, campos: [
    { clave: 'ultima_revision', etiqueta: 'Última revisión del contenido', tipo: 'fecha', ayuda: 'Revisión mensual sugerida.' },
    { clave: 'vencimiento_insumos', etiqueta: 'Vencimiento más próximo de los insumos', tipo: 'fecha' }] },
  gabinete: { etiqueta: 'Gabinete contra incendio', prefijo: 'GAB', emergencia: true, campos: [
    { clave: 'ultima_inspeccion', etiqueta: 'Última inspección', tipo: 'fecha', ayuda: 'Inspección anual sugerida.' },
    { clave: 'prueba_manguera', etiqueta: 'Última prueba de la manguera', tipo: 'fecha' }] },
  otro: { etiqueta: 'Otro', prefijo: 'ACT', emergencia: false, campos: [] },
};
export const LISTA_CATEGORIAS = Object.keys(CATEGORIAS) as Categoria[];
export const etiquetaCategoria = (c: string) => CATEGORIAS[c as Categoria]?.etiqueta ?? c;
export const esEmergencia = (c: string) => !!CATEGORIAS[c as Categoria]?.emergencia;

/** Categorías que hacen aplicable cada ítem del Anexo 1 (GT 11: ventilación; GT 27 y GT 28: máquinas y equipos de producción). */
export const CATEGORIAS_VENTILACION: Categoria[] = ['ventilacion'];
export const CATEGORIAS_MAQUINAS: Categoria[] = ['maquina'];

// ── Estados ──
export const ETIQUETA_ESTADO: Record<EstadoOperativo, string> = {
  operativo: 'Operativo', con_observaciones: 'Con observaciones', fuera_servicio: 'Fuera de servicio', en_mantenimiento: 'En mantenimiento',
};
export const ESTADOS: EstadoOperativo[] = ['operativo', 'con_observaciones', 'fuera_servicio', 'en_mantenimiento'];
export const ETIQUETA_APTITUD: Record<Aptitud, string> = { apto: 'Apto', no_apto: 'No apto' };
export const APTITUDES: Aptitud[] = ['apto', 'no_apto'];
export const ETIQUETA_CRITICIDAD: Record<Criticidad, string> = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
export const CRITICIDADES: Criticidad[] = ['baja', 'media', 'alta', 'critica'];
/** Colores (siempre acompañados del texto). */
export const COLOR_ESTADO: Record<EstadoOperativo, string> = {
  operativo: '#22c55e', con_observaciones: '#eab308', fuera_servicio: '#ef4444', en_mantenimiento: '#38bdf8',
};
export const COLOR_APTITUD: Record<Aptitud, string> = { apto: '#22c55e', no_apto: '#ef4444' };
export const COLOR_CRITICIDAD: Record<Criticidad, string> = { baja: '#94a3b8', media: '#38bdf8', alta: '#f97316', critica: '#ef4444' };

export const ETIQUETA_PERMISO: Record<TipoPermiso, string> = {
  altura: 'Trabajo en altura', caliente: 'Trabajo en caliente', confinado: 'Espacio confinado', electrico: 'Trabajo eléctrico',
  quimico: 'Químico o sustancias peligrosas', loto: 'Bloqueo y etiquetado (LOTO)', excavacion: 'Excavación o zanjas',
  maquinaria: 'Maquinaria pesada', vias: 'Trabajo en vías públicas',
};
export const TIPOS_PERMISO = Object.keys(ETIQUETA_PERMISO) as TipoPermiso[];

export const ETIQUETA_DOCUMENTO: Record<TipoDocumento, string> = {
  manual: 'Manual', ficha_tecnica: 'Ficha técnica', certificado: 'Certificado', garantia: 'Garantía', informe: 'Informe', foto: 'Foto', otro: 'Otro',
};
export const TIPOS_DOCUMENTO = Object.keys(ETIQUETA_DOCUMENTO) as TipoDocumento[];

export const PELIGROS_SUGERIDOS = [
  'Atrapamiento', 'Corte', 'Golpe', 'Proyección de partículas', 'Contacto eléctrico', 'Quemadura', 'Ruido', 'Vibración',
  'Caída de objetos', 'Caída a distinto nivel', 'Presión', 'Incendio o explosión', 'Exposición a químicos', 'Atropello',
];

export const ETIQUETA_EVENTO: Record<EventoHistorial, string> = {
  creado: 'Registro del activo', editado: 'Datos editados', codigo: 'Cambio de código', estado: 'Cambio de estado operativo',
  aptitud: 'Cambio de aptitud de seguridad', archivado: 'Archivado', restaurado: 'Restaurado', documento: 'Documento agregado',
  documento_eliminado: 'Documento eliminado', inspeccion: 'Inspección',
};

/** Nombre visible de cada columna (para el historial "Datos editados"). */
export const ETIQUETA_CAMPO: Record<string, string> = {
  name: 'Nombre', category: 'Categoría', manufacturer: 'Fabricante', brand: 'Marca', model: 'Modelo', serial_number: 'Número de serie',
  manufacture_year: 'Año de fabricación', description: 'Descripción', photo_path: 'Foto', site_id: 'Sede', area: 'Área',
  location_detail: 'Ubicación específica', tech: 'Datos técnicos', acquired_on: 'Fecha de adquisición', installed_on: 'Fecha de instalación',
  useful_life_years: 'Vida útil', manual_ref: 'Manual', responsible_worker_id: 'Trabajador responsable', responsible_user_id: 'Usuario responsable',
  external_provider: 'Proveedor externo', hazards: 'Peligros', required_epp_ids: 'EPP requerido', isolation_points: 'Puntos de aislamiento',
  permit_type: 'Permiso de trabajo', procedure_ref: 'Procedimiento', operating_restrictions: 'Restricciones de operación',
  criticality: 'Criticidad', emergency_sector_id: 'Sector del plan de emergencias',
};

export const BUCKET_ACTIVOS = 'asset-files';
export const SEGUNDOS_URL_ACTIVOS = 120;
export const MAX_BYTES_ARCHIVO = 20 * 1024 * 1024;
export const TIPOS_ARCHIVO = ['application/pdf', 'image/png', 'image/jpeg'];
